import { useCallback, useEffect, useState } from 'react';

import { listKnowledgeCards, type KnowledgeCardResponseDTO } from '@/api/knowledge/api';

export type { KnowledgeCardResponseDTO } from '@/api/knowledge/api';

export interface UseBrowseAssetsResult {
  cards: KnowledgeCardResponseDTO[];
  loading: boolean;
  fetchCards: (q?: string) => Promise<void>;
}

/**
 * Fetches project knowledge cards for the browse-assets drawer.
 * Re-fetches when the drawer opens, and on explicit search queries.
 */
export function useBrowseAssets(
  open: boolean,
  accessToken: string,
  clientId: string,
  projectId: string,
): UseBrowseAssetsResult {
  const [cards, setCards] = useState<KnowledgeCardResponseDTO[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchCards = useCallback(
    async (q?: string) => {
      setLoading(true);
      try {
        const result = await listKnowledgeCards(accessToken, clientId, {
          project_id: projectId,
          q: q?.trim() || undefined,
          page_size: 50,
        });
        setCards(result.items);
      } catch {
        setCards([]);
      } finally {
        setLoading(false);
      }
    },
    [accessToken, clientId, projectId],
  );

  useEffect(() => {
    if (open) {
      void fetchCards();
    }
  }, [open, fetchCards]);

  return { cards, loading, fetchCards };
}
